import { useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';

// Object URLs are cached per capture for the session so scrolling the log
// doesn't refetch thumbnails. Bounded: the oldest entries are revoked once
// the cache passes MAX_CACHED so long sessions don't leak blob memory.
const MAX_CACHED = 150;
const cache = new Map();

/** Thumbnail props for a ledger row: Capture thumb, or page 1 of the Cost Inbox invoice behind it. */
export const rowThumbProps = (row, width = 240) => (
  row?.inbox_item_id && row.inbox_has_file
    ? { endpoint: `/cost-ledger/entries/${row.id}/document/pages/0?width=${width}`, enabled: true, testKey: row.id }
    : { captureId: row?.capture_id, enabled: !!row?.capture_has_thumb }
);

function remember(captureId, url) {
  const prev = cache.get(captureId);
  if (prev && prev !== url) URL.revokeObjectURL(prev);
  cache.delete(captureId);
  cache.set(captureId, url);
  while (cache.size > MAX_CACHED) {
    const [oldestId, oldestUrl] = cache.entries().next().value;
    URL.revokeObjectURL(oldestUrl);
    cache.delete(oldestId);
  }
}

/** Authenticated thumbnail — fetches /captures/{id}/thumb (or any `endpoint`) as a blob. */
export function AuthThumb({ captureId, endpoint, testKey, enabled = true, className = '', size = 'sm', fit = 'cover', placeholderIcon: Placeholder = Receipt, alt = 'Receipt' }) {
  const { api } = useAuth();
  const path = endpoint || (captureId ? `/captures/${captureId}/thumb` : null);
  const key = testKey || captureId || 'none';
  const [src, setSrc] = useState(() => (path && cache.get(path)) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || !path || src) return;
    let alive = true;
    api.get(path, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        remember(path, url);
        if (alive) setSrc(url);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [api, path, enabled, src]);

  if (!enabled || !path || failed || !src) {
    return (
      <div
        className={`flex items-center justify-center ${className}`}
        style={{ background: PAPER.paperMuted, color: PAPER.hint, border: `1px solid ${PAPER.border}` }}
        data-testid={`cost-log-thumb-placeholder-${key}`}
      >
        <Placeholder className={size === 'lg' ? 'w-10 h-10' : 'w-5 h-5'} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`${fit === 'contain' ? 'object-contain' : 'object-cover'} ${className}`}
      style={{ border: `1px solid ${PAPER.border}`, background: fit === 'contain' ? PAPER.paperMuted : undefined }}
      data-testid={`cost-log-thumb-${key}`}
    />
  );
}
